import type { Router, NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useUserStoreWithOut } from '@/store-pinia/modules/user'
import { PageEnum } from '@/enums/pageEnum'
import { usePermissionStoreWithOut } from '@/store-pinia/modules/permission'
import { useBaseStoreWithOut } from '@/store-pinia/modules/base'

import { pageNotFoundRoute } from '@/router/basic'
import { message } from 'ant-design-vue'

const LOGIN_PATH = PageEnum.BASE_LOGIN

const whitePathList: string[] = [LOGIN_PATH]

const extractRegionWarehouseFromUrl = (url: string) => {
  const matchRes = url.match(/wms[-_](\w+)/)
  return matchRes ? matchRes[1].toUpperCase() : ''
}

let isCheckedPhysicalWarehouseCache = false

/**
 * 创建权限守护，动态生成路由
 * @param router
 */
export function createPermissionGuard(router: Router) {
  const userStore = useUserStoreWithOut()
  const permissionStore = usePermissionStoreWithOut()
  const baseStore = useBaseStoreWithOut()

  const goLoginPage = (to: RouteLocationNormalized, next: NavigationGuardNext) => {
    const redirectData: { path: string; replace: boolean; query?: Recordable<string> } = {
      path: LOGIN_PATH,
      replace: true,
    }
    if (to.path) {
      redirectData.query = {
        ...redirectData.query,
        redirect: to.fullPath,
      }
    }
    next(redirectData)
  }

  router.beforeEach(async (to, from, next) => {
    const token = userStore.getToken
    const warehouseCode = baseStore.getWarehouseCode

    // 白名单直接通过
    if (whitePathList.includes(to.path)) {
      if (to.path === LOGIN_PATH && token && warehouseCode) {
        try {
          await userStore.afterLogin()
          next((to.query?.redirect || '/') as string)
          return
        } catch (err) {
          console.log('Permission guard error: ', err)
        }
      }
      next()
      return
    }
    const searchParams = new URLSearchParams(new URL(to.fullPath, location.origin).search)
    const authCode = searchParams.get('AuthCode') as string

    if (authCode) {
      try {
        const warehouseCode = (searchParams.get('regionWarehouseCode') ||
          extractRegionWarehouseFromUrl(location.href)) as string
        console.log('authCode: ', authCode)
        console.log('warehouseCode: ', warehouseCode)
        await userStore.autoLogin(authCode, warehouseCode, false)
        baseStore.setWarehouseCode(warehouseCode)
        next({
          path: (to.query?.redirect || '/') as string,
          replace: true,
        })
        return
      } catch (err: any) {
        if (err.message && typeof err.message == 'string') {
          setTimeout(() => {
            message.error(err.message)
          }, 0)
        }
        next({
          path: LOGIN_PATH,
          replace: true,
        })
        return
      }
    }

    if (!warehouseCode) {
      goLoginPage(to, next)
      return
    }

    if (!token) {
      if (to.meta.ignoreAuth) {
        next()
        return
      }
      goLoginPage(to, next)
      return
    }

    if (baseStore.getWarehouseInfo === null) {
      try {
        await baseStore.initWarehouseInfo()
      } catch (err) {
        console.log('Error: ', err)
      }
    }

    if (userStore.getLastUpdateTime === 0) {
      try {
        await userStore.loadUserInfo()
      } catch (err) {
        next()
        return
      }
    }

    if (!isCheckedPhysicalWarehouseCache) {
      try {
        await baseStore.checkPhysicalWarehouseCache()
        isCheckedPhysicalWarehouseCache = true
      } catch (err) {
        console.log(err)
      }
    }

    if (
      from.path === LOGIN_PATH &&
      to.name === pageNotFoundRoute.name &&
      to.fullPath !== PageEnum.BASE_HOME
    ) {
      next(PageEnum.BASE_HOME)
      return
    }

    if (permissionStore.getIsDynamicAddedRoute) {
      next()
      return
    }

    const routes = await permissionStore.buildRoutes()

    routes.forEach((route) => {
      router.addRoute(route)
    })

    router.addRoute(pageNotFoundRoute)

    permissionStore.setIsDynamicAddedRoute(true)

    if (to.name === pageNotFoundRoute.name) {
      next({ path: to.fullPath, replace: true, query: to.query })
    } else {
      const redirectPath = (from.query.redirect || to.path) as string
      const redirect = decodeURIComponent(redirectPath)
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect }
      next(nextData)
    }
  })
}
